import type { Task } from './types';

/**
 * Sum of input + output Claude tokens used by a task across every
 * agent call. Coerces NUMERIC string values from pg to a real number
 * just in case (defense in depth).
 */
export function totalTokens(task: Pick<Task, 'total_input_tokens' | 'total_output_tokens'>): number {
  return (Number(task.total_input_tokens) || 0) + (Number(task.total_output_tokens) || 0);
}

/**
 * Format a token count compactly. 1,234 stays as "1,234"; bigger
 * numbers collapse to "12.3k" / "1.2M". Keeps task cards uncluttered
 * while still giving a real sense of scale.
 */
export function formatTokens(n: number): string {
  const safe = Number.isFinite(n) ? Math.max(0, Math.trunc(n)) : 0;
  if (safe < 10_000) return safe.toLocaleString();
  if (safe < 1_000_000) return `${(safe / 1_000).toFixed(1)}k`;
  return `${(safe / 1_000_000).toFixed(2)}M`;
}

/**
 * Compact relative-time formatter: "just now", "5m ago", "3h ago",
 * "2d ago", or the locale date for anything older than ~30 days.
 * Used on task cards and detail headers where "created 14:23 today"
 * is less useful than the relative distance.
 */
export function formatRelativeTime(iso: string | null | undefined): string {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return '';
  const diffSec = Math.round((Date.now() - t) / 1000);
  if (diffSec < 5) return 'just now';
  if (diffSec < 60) return `${diffSec}s ago`;
  const diffMin = Math.round(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.round(diffHr / 24);
  if (diffDay < 30) return `${diffDay}d ago`;
  return new Date(iso).toLocaleDateString();
}

/**
 * Absolute local time for tooltips/hover: "May 26, 2026, 3:48 PM".
 * Stable across renders so we use it for `title` attributes.
 */
export function formatAbsoluteTime(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString();
}

/**
 * Duration between two ISO timestamps as "1h 23m" / "45m" / "12s".
 * If `end` is null/undefined, measures from `start` to now — used for
 * the live "running for…" badge on in-flight tasks.
 */
export function formatDuration(
  start: string | null | undefined,
  end: string | null | undefined,
): string {
  if (!start) return '';
  const s = new Date(start).getTime();
  const e = end ? new Date(end).getTime() : Date.now();
  if (!Number.isFinite(s) || !Number.isFinite(e) || e < s) return '';
  const sec = Math.round((e - s) / 1000);
  if (sec < 60) return `${sec}s`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m`;
  const hr = Math.floor(min / 60);
  const remMin = min % 60;
  return remMin === 0 ? `${hr}h` : `${hr}h ${remMin}m`;
}
